<template>
    <teleport to="body">
        <div
            v-if="open"
            class="fixed inset-0 z-[1200] flex items-center justify-center bg-black/45 px-4 py-8"
            @click.self="$emit('close')"
        >
            <div class="flex min-h-[600px] max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
                <div class="flex items-center justify-between border-b border-bordergray-100 px-6 py-5">
                    <div class="text-xl font-semibold leading-none text-gray-900">
                        {{ $t('Dateimanager') }}
                    </div>
                    <button
                        class="inline-flex cursor-pointer items-center justify-center text-gray-400 transition hover:text-gray-700"
                        type="button"
                        :title="$t('Schließen')"
                        :aria-label="$t('Schließen')"
                        @click="$emit('close')"
                    >
                        <svg-vue class="h-5 w-5" icon="font-awesome.regular.xmark"></svg-vue>
                    </button>
                </div>

                <div class="border-b border-bordergray-100 px-6 py-4">
                    <input
                        v-model="search"
                        class="stage-filter-input form-input block w-full text-sm"
                        type="text"
                        :placeholder="$t('Dateien durchsuchen')"
                    >
                </div>

                <div class="min-h-0 flex-1 overflow-y-auto">
                    <div v-if="loading" class="p-6 text-sm text-gray-500">
                        {{ $t('Dateien werden geladen ...') }}
                    </div>

                    <div v-else-if="files.length === 0" class="p-6 text-sm text-gray-500">
                        {{ $t('Keine CMS-Dateien gefunden.') }}
                    </div>

                    <div v-else class="divide-y divide-bordergray-100">
                        <div
                            v-for="file in files"
                            :key="file.uuid || file.id"
                            class="flex items-center gap-4 px-6 py-4"
                        >
                            <div class="flex h-16 w-16 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-gray-200 bg-white">
                                <img
                                    v-if="file.is_image"
                                    :src="file.url"
                                    :alt="file.name"
                                    class="h-full w-full object-cover"
                                >
                                <svg-vue
                                    v-else
                                    :icon="resolveCmsFileIcon(file.extension)"
                                    class="h-8 w-8 text-gray-400"
                                ></svg-vue>
                            </div>
                            <div class="min-w-0 flex-1">
                                <div class="truncate text-base font-medium text-gray-900">{{ file.name }}</div>
                                <div class="mt-1 font-mono text-xs text-gray-400">{{ file.key }}</div>
                                <div class="mt-2 text-sm text-gray-400">
                                    {{ file.extension?.toUpperCase() || 'DATEI' }}
                                    <span v-if="file.size_readable">, {{ file.size_readable }}</span>
                                </div>
                            </div>
                            <button
                                class="inline-flex cursor-pointer items-center rounded bg-gray-800 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-700"
                                type="button"
                                @click="insertFile(file)"
                            >
                                {{ actionLabel }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="flex items-center justify-between border-t border-bordergray-100 bg-surface-raised px-6 py-4 text-sm">
                    <div class="flex items-center gap-3 text-gray-700">
                        <span>{{ paginationLabel }}</span>
                        <div class="flex h-8 min-w-[2.25rem] items-center justify-center rounded border border-bordergray-100 bg-white px-2 text-gray-700">
                            {{ pagination.total || files.length || 0 }}
                        </div>
                    </div>
                    <div class="flex items-center gap-5 text-gray-400">
                        <button
                            class="inline-flex cursor-pointer items-center justify-center text-gray-400 transition hover:text-gray-700 disabled:cursor-default disabled:opacity-40"
                            type="button"
                            :disabled="pagination.currentPage <= 1"
                            :title="$t('Zurück')"
                            :aria-label="$t('Zurück')"
                            @click="loadFiles(pagination.currentPage - 1)"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-left"></svg-vue>
                        </button>
                        <button
                            class="inline-flex cursor-pointer items-center justify-center text-gray-400 transition hover:text-gray-700 disabled:cursor-default disabled:opacity-40"
                            type="button"
                            :disabled="pagination.currentPage >= pagination.totalPages"
                            :title="$t('Weiter')"
                            :aria-label="$t('Weiter')"
                            @click="loadFiles(pagination.currentPage + 1)"
                        >
                            <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-right"></svg-vue>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
import { resolveCmsFileIcon } from "@/utils/cms-file-manager";

export default {
    name: "CmsMediaDrawer",
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        selectionMode: {
            type: String,
            default: 'snippet',
        },
        insertLabel: {
            type: String,
            default: '',
        },
    },
    emits: ['close', 'insert', 'select'],
    data() {
        return {
            loading: false,
            search: '',
            searchDebounce: null,
            files: [],
            pagination: {
                currentPage: 1,
                totalPages: 1,
                perPage: 12,
                total: 0,
            },
        };
    },
    computed: {
        paginationLabel() {
            const total = Number(this.pagination.total || 0);

            if (total < 1) {
                return '0 bis 0';
            }

            const currentPage = Number(this.pagination.currentPage || 1);
            const perPage = Number(this.pagination.perPage || total);
            const from = ((currentPage - 1) * perPage) + 1;
            const to = Math.min(currentPage * perPage, total);

            return `${from} bis ${to}`;
        },
        actionLabel() {
            if (this.insertLabel) {
                return this.insertLabel;
            }

            return this.selectionMode === 'file' ? this.$t('Auswählen') : this.$t('Einfügen');
        },
    },
    watch: {
        open(value) {
            if (value) {
                this.loadFiles(1);
                return;
            }

            this.search = '';
            this.files = [];
            this.pagination = {
                currentPage: 1,
                totalPages: 1,
                perPage: 12,
                total: 0,
            };
        },
        search() {
            if (!this.open) {
                return;
            }

            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => {
                this.loadFiles(1);
            }, 220);
        },
    },
    beforeUnmount() {
        clearTimeout(this.searchDebounce);
    },
    methods: {
        resolveCmsFileIcon,
        async loadFiles(page = 1) {
            if (!this.open) {
                return;
            }

            this.loading = true;

            try {
                const response = await axios.get('api/workspace/admin/cms/file-manager', {
                    params: {
                        page,
                        perPage: 12,
                        search: this.search,
                        sort: JSON.stringify({
                            column: 'name',
                            order: 'asc',
                        }),
                    },
                });

                this.files = response.data?.items || [];
                this.pagination = {
                    currentPage: response.data?.pagination?.currentPage || 1,
                    totalPages: response.data?.pagination?.totalPages || 1,
                    perPage: response.data?.pagination?.perPage || 12,
                    total: response.data?.pagination?.total || 0,
                };
            } catch (error) {
                this.files = [];
                this.pagination = {
                    currentPage: 1,
                    totalPages: 1,
                    perPage: 12,
                    total: 0,
                };
                this.$notify?.({
                    type: 'error',
                    title: this.$t('Fehler'),
                    text: this.$t('CMS-Dateien konnten nicht geladen werden.'),
                });
            } finally {
                this.loading = false;
            }
        },
        insertFile(file) {
            if (this.selectionMode === 'file') {
                this.$emit('select', file);
                this.$emit('close');

                return;
            }

            this.$emit('insert', this.buildImageSnippet(file));
            this.$emit('close');
        },
        buildImageSnippet(file) {
            const key = String(file?.key || '').trim();

            return [
                '<img',
                `    src="{{ file:${key}:url }}"`,
                `    alt="{{ file:${key}:alt }}"`,
                `    title="{{ file:${key}:title }}"`,
                `    width="{{ file:${key}:width }}"`,
                `    height="{{ file:${key}:height }}"`,
                `    data-caption="{{ file:${key}:caption }}"`,
                `    data-source="{{ file:${key}:source }}"`,
                `    data-copyright="{{ file:${key}:copyright }}"`,
                '    loading="lazy"',
                '>',
            ].join('\n');
        },
    },
};
</script>
